<template>
  <div class="mt-2 px-6 draft-job-profiles-list-container">
    <LibrariesSkeletonLoader
      class="ml-6 mr-6"
      :is-loading="libraryLoading"
      is-requisition
    />

    <div
      v-if="!libraryLoading"
    >
      <NoDataBlock
        v-if="!jobProfileDrafts.length"
        label="Draft"
        document-type="Job Profiles"
      >
        <template #description>
          There is no Job Profile data available for your selection.
        </template>
      </NoDataBlock>
      <div v-else>
        <StickyListHeader
          v-if="jobProfileDrafts.length"
          class="draft-list-item-wrapper"
          document-class="item-job-profile-draft-add-checkbox"
          :header-columns="headerColumns"
          :selected-sort="selectedSort"
          :select-all="allSelected"
          :select-all-disabled="!draftsSelectableForCurrentMode.length"
          :sort-direction="sortDirection"
          checkbox-size="md"
          @submit-sort="submitSort"
          @update:select-all="handleSelectAll"
        />
        <LibraryList>
          <LibraryListItemWrapper
            v-for="document in draftDocumentsWithItems"
            :key="document.ulid"
            class="draft-list-item-wrapper"
          >
            <router-link
              :to="{ query: { ...route.query, preview: document.ulid, type: 'jobProfile' } }"
            >
              <DraftJobProfilesItem
                :ulid="document.ulid"
                :title="document.title"
                :job-level="formatJobLevel(document.job_level, { mode: 'short' })"
                :job-code="document.job_code"
                :department="document.department"
                :last-revised="document.created_at"
                :selected="documentIsSelected(document.ulid)"
                :is-archived="Boolean(document.is_archived)"
                :checkbox-disabled="isCheckboxDisabled(document)"
                :checkbox-disabled-reason="getCheckboxDisabledReason(document)"
                show-checkbox
                class="item-job-profile-draft-add-checkbox"
                @update:selected="(value) => handleCheckboxActions(document.ulid, value)"
              />
            </router-link>
            <ThreeDotsMenu
              :menu-items="document.items || []"
              :document="document"
              @click="handleMenuItemClick"
            />
          </LibraryListItemWrapper>
        </LibraryList>
      </div>
    </div>

    <StickyPagination
      v-if="totalPages > 1"
      :model-value="page"
      :length="totalPages"
      @update:model-value="(value) => sendFilterRequest({ pageNumber: value, router })"
    />
  </div>
  <ArchiveModal
    :show="showArchiveModal"
    :name="jobToArchive ? jobToArchive.title : ''"
    :is-archived="Boolean(jobToArchive?.is_archived)"
    :loading="archiveLoading"
    @close="handleCloseArchiveModal"
    @confirm="handleConfirmArchive"
  />
</template>

<script setup>
import {
  ref,
  computed,
  onMounted,
  onBeforeUnmount,
} from 'vue';
import { storeToRefs } from 'pinia';
import { useRoute, useRouter } from 'vue-router';
import { subscribeToFilterUpdates, unsubscribeToFilterUpdates } from '@/helpers/filterDispatcher';
import { useCompareSelectionPrune } from '@/composables/useCompareSelectionPrune';
import { useAppLoading } from '@/stores/app/loading';
import { useJobProfileDrafts } from '@/stores/jobProfiles/jobProfileDrafts';
import { useFilters } from '@/stores/filters/filters';
import { useAppBase } from '@/stores/app/base';
import { useAppAuthData } from '@/stores/app/authData';
import { useAppPagination } from '@/stores/app/pagination';
import { useSubMenu } from '@/stores/filters/subMenu';
import { useAppRoute } from '@/stores/app/route';
import LibrariesSkeletonLoader
  from '@/components/shared/SkeletonLoaders/LibrariesSkeletonLoader.vue';
import LibraryListItemWrapper from '@/components/shared/LibraryListItemWrapper.vue';
import LibraryList from '@/components/shared/LibraryList.vue';
import DraftJobProfilesItem from '@/components/jobProfiles/profileListItems/DraftJobProfilesItem.vue';
import StickyListHeader from '@/components/library/StickyListHeader.vue';
import StickyPagination from '@/components/shared/StickyPagination.vue';
import ThreeDotsMenu from '@/components/shared/ThreeDotsMenu.vue';
import ArchiveModal from '@/components/shared/ArchiveModal.vue';
import NoDataBlock from '@/components/library/NoDataBlock.vue';
import { stickyHeadersJobProfiles, getStickyHeaderSortColumnLabel } from '@/helpers/constants/stickyHeaders';
import { EDIT_ICON, ARCHIVE_ICON } from '@/helpers/constants/librariesMenuIcons';
import { useTracking } from '@/stores/tracking/tracking';
import { JP_PAGE_DRAFT_LIBRARY } from '@/helpers/jobProfileLibraryTracking';
import { formatJobLevel } from '@/helpers/jobLevelDisplay';
import { connectedDraftArchiveTooltipText, connectedDraftUnarchiveTooltipText } from '@/helpers/tooltips';

const emit = defineEmits(['track-page-load']);

const route = useRoute();
const router = useRouter();
const { previousRoute } = storeToRefs(useAppRoute());
const { isStandaloneJobNav } = storeToRefs(useAppAuthData());

const { pageLoading, listLoading } = storeToRefs(useAppLoading());
const {
  jobProfileDrafts, selectedProfiles, textFilter, hasSelectedArchived, hasSelectedActive,
} = storeToRefs(useJobProfileDrafts());
const {
  loadJobProfileDrafts,
  archiveJobProfileByUlid,
  unarchiveJobProfileByUlid,
  setTextFilter,
  resetSelectedProfiles,
  resetStatusDefault,
} = useJobProfileDrafts();
const {
  sendFilterRequest,
  submitSubMenu,
  setFilterConfig,
} = useFilters();
const { showFilterBarSettings } = useAppBase();
const { totalPages, page } = storeToRefs(useAppPagination());
const { selectedSubMenuOption } = storeToRefs(useSubMenu());
const { setSelectedSubMenuOption } = useSubMenu();
const { pruneSelectionIfFiltersChanged } = useCompareSelectionPrune();

const jobToArchive = ref(null);
const showArchiveModal = ref(false);
const archiveLoading = ref(false);

const headerColumns = computed(() => {
  const draftHeaders = [
    'select',
    'title',
    'jobCode',
    'level',
    'department',
    'lastRevised',
  ];

  return draftHeaders.map((header) => stickyHeadersJobProfiles[header]);
});

const selectedSort = computed(() => {
  const fallback = route.query?.sort || 'created_at';
  return (selectedSubMenuOption.value || fallback).replace('-', '');
});

const sortDirection = computed(() => (
  (selectedSubMenuOption.value || route.query?.sort || 'created_at').startsWith('-')
    ? 'desc'
    : 'asc'
));

const isArchivedDraft = (row) => Boolean(row?.is_archived);

// Only archived drafts show when the Status filter is just Archived.
const isArchivedOnlyView = computed(() => route.query?.status === 'archived');

const isCheckboxDisabled = (row) => {
  if (!isStandaloneJobNav.value) return false;
  if (hasSelectedArchived.value && !isArchivedDraft(row)) return true;
  if (hasSelectedActive.value && isArchivedDraft(row)) return true;

  return false;
};

const getCheckboxDisabledReason = (row) => {
  if (hasSelectedArchived.value && !isArchivedDraft(row)) {
    return 'Active drafts cannot be selected while archived drafts are already selected.';
  }

  if (hasSelectedActive.value && isArchivedDraft(row)) {
    return 'Archived drafts cannot be selected while active drafts are already selected.';
  }

  return null;
};

const draftsSelectableForCurrentMode = computed(() => {
  const list = jobProfileDrafts.value;

  if (!list?.length) return [];

  // Integrated has no bulk actions, only Compare, so any draft can be selected.
  if (!isStandaloneJobNav.value) return list;

  if (hasSelectedArchived.value) {
    return list.filter((row) => isArchivedDraft(row));
  }

  if (hasSelectedActive.value) {
    return list.filter((row) => !isArchivedDraft(row));
  }

  if (isArchivedOnlyView.value) {
    return list.filter((row) => isArchivedDraft(row));
  }

  return list.filter((row) => !isArchivedDraft(row));
});

const allSelected = computed(() => {
  const target = draftsSelectableForCurrentMode.value;

  return target.length > 0
    && target.every(({ ulid }) => selectedProfiles.value.has(ulid));
});

const documentIsSelected = (ulid) => selectedProfiles.value.has(ulid);

const getDocumentsWithItems = (profile) => {
  const editItem = { text: 'Edit', icon: EDIT_ICON, value: 'edit' };
  const isConnected = Boolean(profile.hris_reference_ulid);

  // A draft linked to a published profile follows that profile, so it cannot be archived alone.
  // Standalone hides the option. Integrated shows it greyed out with the reason.
  if (isConnected && isStandaloneJobNav.value) {
    return [editItem];
  }

  if (profile.is_archived) {
    return [editItem, {
      text: 'Unarchive',
      icon: ARCHIVE_ICON,
      value: 'unarchive',
      disabled: isConnected,
      tooltip: isConnected ? connectedDraftUnarchiveTooltipText : '',
    }];
  }

  return [editItem, {
    text: 'Archive',
    icon: ARCHIVE_ICON,
    value: 'archive',
    disabled: isConnected,
    tooltip: isConnected ? connectedDraftArchiveTooltipText : '',
  }];
};

const draftDocumentsWithItems = computed(() => jobProfileDrafts.value.map((profile) => ({
  ...profile, items: getDocumentsWithItems(profile),
})));

const submitSort = (selectedId) => {
  const current = selectedSubMenuOption.value || route.query?.sort || '-created_at';
  const currentBase = current.replace('-', '');

  if (selectedId === '__select') return;

  let optionId;

  if (selectedId === currentBase) {
    optionId = current.startsWith('-') ? selectedId : `-${selectedId}`;
  } else {
    optionId = selectedId;
  }

  const direction = optionId.startsWith('-') ? 'desc' : 'asc';

  useTracking().jpLibSortedTable({
    page: JP_PAGE_DRAFT_LIBRARY,
    column: getStickyHeaderSortColumnLabel(optionId, stickyHeadersJobProfiles),
    direction,
  });

  submitSubMenu({ optionId, router });
};

const handleCheckboxActions = (ulid) => {
  if (selectedProfiles.value.has(ulid)) {
    selectedProfiles.value.delete(ulid);
  } else selectedProfiles.value.add(ulid);
};

const handleSelectAll = (value) => {
  if (!value) {
    selectedProfiles.value = new Set();

    return;
  }

  selectedProfiles.value = new Set(
    draftsSelectableForCurrentMode.value.map(({ ulid }) => ulid),
  );
};

const libraryLoading = computed(() => pageLoading.value || listLoading.value);

const loadPage = async (params) => {
  const searchTerm = textFilter.value || params.searchTerm;
  const mode = params.mode === 'draft-job-profiles' ? 'draft' : params.mode;

  const updatedParams = {
    ...params,
    mode,
    searchTerm,
  };

  await loadJobProfileDrafts(updatedParams);

  selectedProfiles.value = pruneSelectionIfFiltersChanged(selectedProfiles.value, {
    searchTerm,
    selectedFilters: params.selectedFilters,
    visibleUlids: jobProfileDrafts.value.map(({ ulid }) => ulid),
  });
};

const reloadDraftList = async () => {
  await loadPage({
    routeQuery: route.query,
    router,
    page: route.query?.page || 1,
    mode: 'draft',
    searchTerm: route.query?.q || '',
    selectedMyJobs: route.query?.owned,
  });
};

const handleCloseArchiveModal = () => {
  showArchiveModal.value = false;
  jobToArchive.value = null;
};

const handleConfirmArchive = async () => {
  const job = jobToArchive.value;

  if (!job?.ulid) return;

  archiveLoading.value = true;

  const wasArchived = Boolean(job.is_archived);
  const done = wasArchived
    ? await unarchiveJobProfileByUlid({ ulid: job.ulid })
    : await archiveJobProfileByUlid({ ulid: job.ulid });

  archiveLoading.value = false;

  handleCloseArchiveModal();

  if (!done) return;

  if (wasArchived) {
    useTracking().jpLibUnarchivedDraft({ page: JP_PAGE_DRAFT_LIBRARY });
  } else {
    useTracking().jpLibArchivedDraft({ page: JP_PAGE_DRAFT_LIBRARY });
  }

  await reloadDraftList();
};

const handleMenuItemClick = (value, job) => {
  const { ulid } = job;

  const actions = {
    edit: () => {
      useTracking().jpLibClickedEditDraft({ page: JP_PAGE_DRAFT_LIBRARY });

      router.push({ name: 'job-profile-view', params: { id: ulid } });
    },
    archive: () => {
      useTracking().jpLibClickedArchiveDraft({ page: JP_PAGE_DRAFT_LIBRARY });

      jobToArchive.value = job;
      showArchiveModal.value = true;
    },
    unarchive: () => {
      useTracking().jpLibClickedUnarchiveDraft({ page: JP_PAGE_DRAFT_LIBRARY });

      jobToArchive.value = job;
      showArchiveModal.value = true;
    },
  };

  if (actions[value]) {
    actions[value]();
  }
};

onMounted(async () => {
  if (previousRoute.value?.name !== 'job-architecture-compare') {
    resetSelectedProfiles();
  }

  subscribeToFilterUpdates(loadPage);

  setFilterConfig({
    module: 'jobProfiles',
    showMyJobs: true,
    datepicker: false,
  });

  showFilterBarSettings({ route: route.name });

  if (route.query?.q) setTextFilter(route.query.q);

  await loadPage({
    routeQuery: route.query,
    router,
    page: route.query?.page || 1,
    mode: 'draft',
    searchTerm: route.query?.q || '',
    selectedMyJobs: route.query?.owned,
  });

  emit('track-page-load', { page: 'Job Profile Drafts: Open' });
});

onBeforeUnmount(() => {
  unsubscribeToFilterUpdates(loadPage);

  setFilterConfig({
    subMenuType: null,
  });

  if (route.name !== 'job-architecture-compare') {
    resetSelectedProfiles();
  }

  setSelectedSubMenuOption('');
  setTextFilter('');
  resetStatusDefault();
});
</script>

<style lang="less" scoped>
.draft-list-item-wrapper {
  display: grid;
  grid-template-columns: 1fr 50px;
}

.item-job-profile-draft, :deep(.item-job-profile-draft) {
  grid-template-columns: minmax(260px, 1fr) minmax(80px, 120px) minmax(150px, 250px) 120px 120px;
}

.item-job-profile-draft-add-checkbox, :deep(.item-job-profile-draft-add-checkbox) {
  grid-template-columns: 50px minmax(260px, 1fr) minmax(80px, 120px) minmax(150px, 250px) 120px 120px;
}

:deep(.item-job-profile-draft-add-checkbox) .select-all-header {
  padding-left: 0;
  margin: 0 auto;
}
</style>
